import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../constants/fonts';
import type { TicketingAnalyticsTeamOption, TicketingAnalyticsTeamScope } from '../../components/types';

const ALL_TEAMS_OPTION: TicketingAnalyticsTeamOption = { id: '__all__', name: 'All' };

interface TeamSelectorDropdownProps {
  /** Every team available to the signed-in user, from the team catalog. */
  teams: TicketingAnalyticsTeamOption[];
  /** Currently selected scope. `id: null` represents "All". */
  value: TicketingAnalyticsTeamScope | null;
  onChange: (scope: TicketingAnalyticsTeamScope) => void;
  loading?: boolean;
}

/**
 * Global Team selector for Ticketing Analytics — displayed separately from
 * the filter bar (per spec). Mirrors the Sustain Queue header's team
 * switcher pill + portal-rendered menu, generalized with an explicit "All"
 * option at the top of the list.
 */
const TeamSelectorDropdown: React.FC<TeamSelectorDropdownProps> = ({ teams, value, onChange, loading }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerId = 'ta-team-switcher-trigger';
  const menuId = 'ta-team-switcher-menu';

  const repositionMenu = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const el = document.getElementById(triggerId);
    if (el) {
      const rect = el.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(200, rect.width) });
    }
  }, []);

  const toggleMenu = () => {
    repositionMenu();
    setMenuOpen((v) => !v);
  };

  useEffect(() => {
    if (!menuOpen || Platform.OS !== 'web') return;
    const handleClick = (e: MouseEvent) => {
      const trigger = document.getElementById(triggerId);
      const menu = document.getElementById(menuId);
      const target = e.target as Node;
      if ((!trigger || !trigger.contains(target)) && (!menu || !menu.contains(target))) {
        setMenuOpen(false);
      }
    };
    const handleRepos = () => repositionMenu();
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('resize', handleRepos);
    window.addEventListener('scroll', handleRepos, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('resize', handleRepos);
      window.removeEventListener('scroll', handleRepos, true);
    };
  }, [menuOpen, repositionMenu]);

  const isAllSelected = value?.id == null;
  const displayName = loading ? 'Loading...' : value?.name ?? 'All';

  const handleSelect = (option: TicketingAnalyticsTeamOption) => {
    onChange(option.id === ALL_TEAMS_OPTION.id ? { id: null, name: 'All' } : { id: option.id, name: option.name });
    setMenuOpen(false);
  };

  const menuItems = [ALL_TEAMS_OPTION, ...teams].map((team) => {
    const active = team.id === ALL_TEAMS_OPTION.id ? isAllSelected : value?.id === team.id;
    return (
      <Pressable
        key={team.id}
        style={[styles.menuItem, active && styles.menuItemActive]}
        onPress={() => handleSelect(team)}
      >
        <Text style={[styles.menuItemText, active && styles.menuItemTextActive]}>{team.name}</Text>
        {active ? <MaterialIcons name="check" size={16} color="#005CBC" /> : null}
      </Pressable>
    );
  });

  return (
    <View style={{ position: 'relative' }}>
      <Pressable
        // @ts-ignore web id
        id={triggerId}
        nativeID={triggerId}
        style={styles.trigger}
        onPress={toggleMenu}
        disabled={loading}
        accessibilityRole="button"
        accessibilityLabel="Select Team"
      >
        <Text style={styles.label}>TEAM</Text>
        <View style={styles.dot} />
        {loading ? (
          <ActivityIndicator size="small" color="#005CBC" />
        ) : (
          <Text style={styles.triggerText} numberOfLines={1}>{displayName}</Text>
        )}
        <MaterialIcons name={menuOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'} size={16} color="#4b4b4b" />
      </Pressable>

      {menuOpen && Platform.OS !== 'web' && <View style={styles.menu}>{menuItems}</View>}
      {menuOpen && Platform.OS === 'web' && createPortal(
        <div
          id={menuId}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, minWidth: menuPos.width, zIndex: 9999 }}
        >
          <View style={styles.menu}>{menuItems}</View>
        </div>,
        document.body,
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#d0d0d0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
    maxWidth: 240,
  },
  label: {
    fontSize: 10,
    fontFamily: FontFamily.bold,
    color: '#8A8E94',
    letterSpacing: 0.4,
  },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#005CBC' },
  triggerText: { fontSize: 13, fontFamily: FontFamily.bold, color: '#1a2332' },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 4,
    minWidth: 220,
    maxHeight: 320,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingVertical: 4,
    zIndex: 200,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } as any : { elevation: 6 }),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  menuItemActive: {
    backgroundColor: '#F0F6FF',
  },
  menuItemText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
  },
  menuItemTextActive: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
});

export default TeamSelectorDropdown;
